import { CheckboxGroup as CompassCheckboxGroup } from "@circleco/compass/components/CheckboxGroup";
import type { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";

import {
  CheckboxGroup as UIKitCheckboxGroup,
  type CheckboxGroupProps,
} from "@/react/components/shared/uikit/Form/CheckboxGroup";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

interface CompassInjectedProps {
  description?: ReactNode;
  error?: string;
  invalid?: boolean;
  legend?: ReactNode;
  legendTooltip?: ReactNode;
  required?: boolean;
}

type CheckboxGroupCompatProps = CheckboxGroupProps & CompassInjectedProps;

const hasUnsupportedOptionFeatures = (options: CheckboxGroupProps["options"]) =>
  options.some(option => Boolean(option.richLabel));

// Compass requires string values, but legacy profile field choices can use
// numeric IDs. Normalize at this boundary and restore originals for RHF state.
const toCompassValue = (
  value: CheckboxGroupProps["options"][number]["value"],
) => String(value ?? "");

const toCompassOption = (
  option: CheckboxGroupProps["options"][number] & { disabled?: boolean },
) => ({
  label: option.label,
  value: toCompassValue(option.value),
  description: option.description,
  disabled: option.disabled,
});

const toCompassValues = (value: unknown) =>
  Array.isArray(value) ? value.map(toCompassValue) : [];

const toFormValues = (
  selectedValues: string[],
  options: CheckboxGroupProps["options"],
) =>
  selectedValues.map(
    selectedValue =>
      options.find(option => toCompassValue(option.value) === selectedValue)
        ?.value ?? selectedValue,
  );

export const CheckboxGroup = (props: CheckboxGroupCompatProps) => {
  const { control } = useFormContext();
  const isCircleV4 = useIsCircleV4FeatureEnabled(
    "circle_v4_form_elements_enabled",
  );

  const {
    name,
    rules,
    options,
    isTooltip,
    uncheckDisabled: isUncheckDisabled,
    legend,
    legendTooltip,
    error,
    invalid: isInvalid,
    required: isRequired,
  } = props;

  if (
    !isCircleV4 ||
    isTooltip ||
    isUncheckDisabled ||
    hasUnsupportedOptionFeatures(options)
  ) {
    return <UIKitCheckboxGroup {...props} />;
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value } }) => (
        <CompassCheckboxGroup
          value={toCompassValues(value)}
          onValueChange={selectedValues =>
            onChange(toFormValues(selectedValues, options))
          }
          options={options.map(toCompassOption)}
          legend={legend}
          legendTooltip={legendTooltip}
          error={error}
          invalid={isInvalid}
          required={isRequired}
        />
      )}
    />
  );
};

CheckboxGroup.displayName = "Form.CheckboxGroup";
